import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Trophy, Newspaper } from "lucide-react";
import { API } from "@/lib/gp";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const READ_KEY = "gp_read_notifs";

function getRead() {
  try { return JSON.parse(localStorage.getItem(READ_KEY) || "[]"); } catch { return []; }
}

function storeRead(ids) {
  const unique = [...new Set(ids.filter(Boolean))];
  try { localStorage.setItem(READ_KEY, JSON.stringify(unique)); } catch { /* ignore */ }
  return unique;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [read, setRead] = useState(getRead);
  const navigate = useNavigate();

  const persistRead = useCallback((ids) => {
    const next = storeRead([...getRead(), ...ids]);
    setRead(next);
    fetch(`${API}/notifications/read`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    fetch(`${API}/notifications`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.items) return;
        setItems(data.items);
        if (Array.isArray(data.read_ids) && data.read_ids.length) {
          setRead(storeRead([...getRead(), ...data.read_ids]));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 120000);
    return () => clearInterval(timer);
  }, [load]);

  const unread = items.filter((item) => !read.includes(item.id)).length;

  const markVisibleRead = () => {
    if (!items.length) return;
    persistRead(items.map((item) => item.id));
  };

  return (
    <DropdownMenu onOpenChange={() => { markVisibleRead(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-white/5 transition-colors" aria-label="Notifications">
          <Bell size={19} className="text-white/80" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-gp-red text-white text-[10px] font-bold ring-2 ring-gp-deep">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-gp-surface border-white/10 text-white w-80 p-0">
        <div className="px-4 py-3 border-b border-white/10 font-unbounded font-bold text-sm">Notifications</div>
        <div className="max-h-80 overflow-y-auto gp-scroll" data-testid="notif-list">
          {items.length === 0 && <p className="px-4 py-6 text-sm text-white/40">You're all caught up.</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => navigate(n.to)} className="w-full text-left flex gap-3 px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-gp-blue/15 text-gp-blue shrink-0">
                {n.icon === "trophy" ? <Trophy size={15} /> : <Newspaper size={15} />}
              </span>
              <span>
                <p className="text-sm font-semibold leading-tight">{n.title}</p>
                <p className="text-xs text-white/55 mt-0.5 leading-snug">{n.body}</p>
              </span>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
